import { Delete, DeleteForever, Visibility } from '@mui/icons-material';
import React, { useEffect } from 'react';

import CButton from '../../components/CButton';
import CDeleteConfirmationButton from '../../components/CDeleteConfirmationButton';
import CKebabBar from '../../components/CKebabBar';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CModal from '../../components/CModal';
import CPageModule from '../../components/CPageModule';
import CPageModulePaginatedTable from '../../components/CPageModulePaginatedTable';
import CStatefulContainer from '../../components/CStatefulContainer';
import Page from '../../components/layout/Page';
import XDDropdownItem from '../../components/selection/XDDropdownItem';
import addCustomTableColumn from '../../helpers/addCustomTableColumn';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../models/IRequestParams';
import type { IRequestState } from '../../models/IRequestState';
import type { IResponse } from '../../models/IResponse';
import type { ISortingMeta } from '../../models/ISortingMeta';
import type { TPandasTable } from '../../models/TPandasTable';
import CachingService from '../../services/SvCachingService';
import { updateSnackbar } from '../../store/appSlice';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasTable>> & IRequestParams;
  deleteState?: IRequestState;
  previewOpen: boolean;
  previewState: IRequestState;
  previewContent?: string | Record<any, any>;
  selectedCacheLabel?: string;
  recordToBeDeleted?: string;
  modalOpen: boolean;
}

export default function CacheClearPage() {
  const dispatch = useAppDispatch();
  const { locale } = useAppSelector((state) => state.dashboard);

  const errorMessage = CLocalizedTextStringStatic('globalErrorUnknown');
  const cacheManagementSuccessResponse = CLocalizedTextStringStatic('cacheManagement.successResponse');

  const defaultSorting: ISortingMeta = {
    sortCol: 0,
    ascending: 'ascending'
  };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    },
    previewOpen: false,
    previewState: { isLoading: false, isError: false },
    previewContent: undefined,
    selectedCacheLabel: undefined,
    recordToBeDeleted: undefined,
    modalOpen: false
  });

  const fetchCaches = async (page: number, sorting?: ISortingMeta) => {
    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () => {
        return await CachingService.getCaches({
          page,
          sorting: sorting ?? defaultSorting
        });
      }
    );

    updateStateAttribute<State>(
      {
        snapshot: {
          ...(await getState()).snapshot,
          params: { sorting: sorting ?? defaultSorting }
        }
      },
      setState
    );
  };

  const clearCacheHandler = async () => {
    updateStateAttribute<State>({ deleteState: { isError: false, isLoading: true } }, setState);

    const response = await CachingService.clearCache();
    const isSuccess = await response.isSuccess();

    updateStateAttribute<State>({ deleteState: { isError: false, isLoading: false } }, setState);

    if (isSuccess) {
      const data = await response.getData();

      dispatch(
        updateSnackbar({
          message: data?.deleted_count + ' ' + cacheManagementSuccessResponse,
          severity: 'success',
          isVisible: true
        })
      );

      await fetchCaches(1, state.snapshot.params?.sorting ?? defaultSorting);
    } else {
      updateStateAttribute<State>(
        { deleteState: { isLoading: false, isError: true, strError: errorMessage } },
        setState
      );

      dispatch(
        updateSnackbar({
          message: errorMessage,
          severity: 'error',
          isVisible: true
        })
      );
    }
  };

  useEffect(() => {
    (async () => {
      await fetchCaches(1, defaultSorting);
    })();
  }, [locale]);

  const openPreview = async (cacheLabel: string) => {
    updateStateAttribute<State>(
      {
        previewOpen: true,
        previewState: { isLoading: true, isError: false },
        previewContent: undefined,
        selectedCacheLabel: cacheLabel
      },
      setState
    );

    const res = await CachingService.getCache(cacheLabel);

    let previewContent: string = (await res.getData())?.value ?? '';

    try {
      previewContent = JSON.parse(previewContent);
    } catch (e) {
      if (e instanceof SyntaxError) {
        // Pass
      }
    }

    updateStateAttribute<State>(
      {
        previewState: {
          isLoading: false,
          isError: !(await res.isSuccess())
        },
        previewContent
      },
      setState
    );
  };

  const deleteCache = async () => {
    if (!state.recordToBeDeleted) return;

    const res = await CachingService.deleteCache(state.recordToBeDeleted);

    dispatch(
      updateSnackbar({
        message: await res.getMessage(),
        severity: !(await res.isSuccess()) ? 'error' : 'success',
        isVisible: true
      })
    );

    updateStateAttribute<State>(
      {
        modalOpen: false,
        recordToBeDeleted: undefined
      },
      setState
    );

    if (await res.isSuccess()) {
      await fetchCaches(1, state.snapshot.params?.sorting ?? defaultSorting);
    }
  };

  const apiData = state.snapshot as unknown as {
    data?: IAugmentedBackendResponse<TPandasTable>;
  };

  const results = apiData?.data?.results;
  const resultColumns = results ? Object.values(results) : [];
  const resultsForTable: TPandasTable | undefined = results && resultColumns.length >= 2 ? results : undefined;

  const tableData = resultsForTable
    ? addCustomTableColumn(resultsForTable, '', (_rowIndex, rowArray) => {
        const cacheLabel = rowArray[0] as string;

        return (
          <div className={'flex justify-end gap-3'}>
            <CButton
              propagateClick={false}
              style={{ borderWidth: 1 }}
              onClick={async (e) => {
                e.preventDefault();
                await openPreview(cacheLabel);
              }}
            >
              <Visibility />
            </CButton>

            <CButton
              propagateClick={false}
              style={{ borderWidth: 1 }}
              onClick={(e) => {
                e.preventDefault();
                updateStateAttribute<State>(
                  {
                    modalOpen: true,
                    recordToBeDeleted: cacheLabel
                  },
                  setState
                );
              }}
            >
              <Delete />
            </CButton>
          </div>
        );
      })
    : undefined;

  const tableResponse =
    apiData?.data && tableData
      ? {
          ...apiData.data,
          results: tableData
        }
      : undefined;

  return (
    <Page>
      <CPageModule>
        <div className={'flex items-start justify-between'}>
          <h1>
            <CLocalizedText dictKey={'cacheManagement.section.header'} />
          </h1>

          <CKebabBar
            dropdownItems={
              <XDDropdownItem
                onClick={async () => {
                  if (state.deleteState?.isLoading) return;
                  await clearCacheHandler();
                }}
              >
                <DeleteForever />
                <CLocalizedText dictKey={'cacheManagement.clearAllCache'} />
              </XDDropdownItem>
            }
          >
            <></>
          </CKebabBar>
        </div>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CStatefulContainer
          submodule
          apiRequestState={state.snapshot.state}
          showContent={!state.snapshot.state.isLoading}
        >
          <CPageModulePaginatedTable
            state={state.snapshot.state}
            data={tableResponse}
            params={state.snapshot.params}
            colSpanArr={[4.5, 1.5, 1]}
            colAlignmentArr={['left', 'center', 'right']}
            colFormatArr={[undefined, { in: 'tzTimestamp', out: 'localTimestampExtended' }, undefined]}
            onChange={async (page, sorting) => {
              await fetchCaches(page, sorting);
            }}
          />
        </CStatefulContainer>
      </CPageModule>

      <CModal
        open={state.previewOpen}
        onClose={() => {
          updateStateAttribute<State>(
            {
              previewOpen: false,
              previewContent: undefined,
              selectedCacheLabel: undefined
            },
            setState
          );
        }}
      >
        <CPageModule
          fullWidth
          style={{ paddingTop: 0, paddingBottom: 0 }}
          apiRequestState={state.previewState}
          showContent={!!state.previewContent}
        >
          <h3>{state.selectedCacheLabel}</h3>
          <pre className={'whitespace-pre-wrap'}>{JSON.stringify(state.previewContent, undefined, 2)}</pre>
        </CPageModule>
      </CModal>

      <CDeleteConfirmationButton
        showModal={state.modalOpen}
        onConfirm={deleteCache}
        onCancel={() => {
          updateStateAttribute<State>({ modalOpen: false, recordToBeDeleted: undefined }, setState);
        }}
        title={CLocalizedTextStringStatic('cacheManagement.buttonText')}
      />
    </Page>
  );
}
